import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import theme from 'theme'

export const StatCardWrapper = styled(Box)`
  background: ${theme.colors.surfaceContainerBackground};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  box-sizing: border-box;
  min-height: 162px;

  .stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;

    .stat-heading {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;
      min-width: 0;
    }

    .stat-icon {
      display: inline-flex;
      width: 20px;
      height: 20px;
      align-items: center;
      justify-content: center;
      color: ${theme.colors.menuItemText};
      flex-shrink: 0;
    }

    .stat-title {
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.header};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: normal;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Heroicons paint with stroke="currentColor", so the hover swap is a colour change. */
    .stat-info {
      all: unset;
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      cursor: pointer;
      color: ${theme.colors.menuItemText};

      svg {
        width: ${theme.iconSize.xxSmall}px;
        height: ${theme.iconSize.xxSmall}px;
      }

      &:hover,
      &:focus-visible {
        color: ${theme.colors.cta};
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.cta};
        outline-offset: 2px;
        border-radius: ${theme.attributes.borderRadius.circle};
      }
    }
  }

  .stat-body {
    flex: 1;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;
  }

  .stat-value {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 40px;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 60px;
    font-variant-numeric: tabular-nums;
  }

  .stat-sublabel {
    color: ${theme.colors.textCaption};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;

    .delta-up {
      color: ${theme.colors.statusValueGood};
    }

    .delta-down {
      color: ${theme.colors.statusValueBad};
    }
  }
`

/* Recently added and the breakdown columns filter the grid, so they read as controls. */
export const ClickableStatCard = styled(StatCardWrapper)`
  border: none;
  padding: 0;
  background: none;
  min-height: 0;

  .stat-action {
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
    width: 100%;
    min-height: 162px;
    padding: ${theme.spacing.medium}px;
    background: ${theme.colors.surfaceContainerBackground};
    border: 1px solid ${theme.colors.borderMedium};
    border-radius: ${theme.attributes.borderRadius.tertiary};

    &:hover {
      background: ${theme.colors.devicesRowHoverBg};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.cta};
      outline-offset: 1px;
    }
  }
`

export const ChartCardWrapper = styled(StatCardWrapper)`
  min-height: 348px;
  gap: ${theme.spacing.small}px;

  .chart-body {
    flex: 1;
    min-height: 220px;
    display: flex;
  }

  .chart-empty {
    margin: auto;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
  }
`

/* Figma chart hover: primary background, Stroke/secondary border, pop shadow, 14/600 text. */
export const ChartTooltipBox = styled(Box)`
  background: ${theme.colors.pageBackground};
  border: 1px solid ${theme.colors.dropdownBorder};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.small}px;
  box-shadow: ${theme.colors.tooltipShadow};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.md}px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.extraThick};
  line-height: 20px;

  .tooltip-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .tooltip-label {
    color: ${theme.colors.devicesSecondaryText};
  }

  .tooltip-value {
    color: ${theme.colors.menuItemText};
    font-variant-numeric: tabular-nums;
  }
`

export const StatSkeletonBar = styled(Box)`
  height: 40px;
  width: 120px;
  border-radius: ${theme.attributes.borderRadius.small};
  background: ${theme.colors.cardFillTertiary};
`
